/**
 * MODULE: domain/sales-intelligence/frontend/utils/insightSignalNormalize.ts
 * PURPOSE: Defensive normalization of buying-signal arrays from API/DB JSON (partial merge, sparse rows).
 * DEPENDENCIES: contracts/sales-intelligence.types
 * PUBLIC_API: normalizeFundingSignals, normalizeHiringSignals, normalizeTechSignals, normalizeNewsSignals
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 107 / 200 lines
 */

import type {
  FundingSignal,
  HiringSignal,
  NewsSignal,
  TechChangeSignal,
} from '../../contracts/sales-intelligence.types';

function asObject(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

export function normalizeFundingSignals(v: unknown): FundingSignal[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((item) => {
      const o = asObject(item);
      if (!o) return null;
      const investors = o.investors;
      return {
        date: String(o.date ?? ''),
        amount: String(o.amount ?? ''),
        round: String(o.round ?? ''),
        investors: Array.isArray(investors) ? investors.map((x) => String(x)) : [],
        source_url: String(o.source_url ?? ''),
      };
    })
    .filter(Boolean) as FundingSignal[];
}

export function normalizeHiringSignals(v: unknown): HiringSignal[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((item) => {
      const o = asObject(item);
      if (!o) return null;
      const rc = o.role_count;
      return {
        department: String(o.department ?? ''),
        role_count: typeof rc === 'number' && !Number.isNaN(rc) ? rc : Number(rc) || 0,
        seniority_level: String(o.seniority_level ?? ''),
        detected_at: String(o.detected_at ?? ''),
        source_url: String(o.source_url ?? ''),
      };
    })
    .filter(Boolean) as HiringSignal[];
}

export function normalizeTechSignals(v: unknown): TechChangeSignal[] {
  if (!Array.isArray(v)) return [];
  const allowed: TechChangeSignal['change_type'][] = ['added', 'removed', 'upgraded'];
  return v
    .map((item) => {
      const o = asObject(item);
      if (!o) return null;
      const ct = String(o.change_type ?? 'added');
      const change_type = (allowed.includes(ct as TechChangeSignal['change_type'])
        ? ct
        : 'added') as TechChangeSignal['change_type'];
      const rel = o.relevance_score;
      const relevance_score =
        typeof rel === 'number' && !Number.isNaN(rel) ? rel : Number(rel) || 0;
      return {
        technology: String(o.technology ?? ''),
        change_type,
        detected_at: String(o.detected_at ?? ''),
        relevance_score,
      };
    })
    .filter(Boolean) as TechChangeSignal[];
}

export function normalizeNewsSignals(v: unknown): NewsSignal[] {
  if (!Array.isArray(v)) return [];
  const sentiments: NewsSignal['sentiment'][] = ['positive', 'neutral', 'negative'];
  return v
    .map((item) => {
      const o = asObject(item);
      if (!o) return null;
      const sent = String(o.sentiment ?? 'neutral');
      const sentiment = (sentiments.includes(sent as NewsSignal['sentiment'])
        ? sent
        : 'neutral') as NewsSignal['sentiment'];
      const rel = o.relevance_score;
      const relevance_score =
        typeof rel === 'number' && !Number.isNaN(rel) ? rel : Number(rel) || 0;
      return {
        headline: String(o.headline ?? ''),
        summary: String(o.summary ?? ''),
        published_at: String(o.published_at ?? ''),
        sentiment,
        relevance_score,
        source_url: String(o.source_url ?? ''),
      };
    })
    .filter(Boolean) as NewsSignal[];
}
